import React, { useEffect, useRef } from "react";
import { useIsTouch } from "../lib/useIsTouch";
import { usePrefersReducedMotion } from "../lib/usePrefersReducedMotion";
import { CURSOR } from "../constants/testIds";

/*
 * CustomCursor — light + fast. A coral dot that tracks the pointer instantly
 * (no lag) plus a ring that trails it slightly and grows over interactive
 * elements. Only `transform` is animated (GPU-composited, no per-frame layout),
 * and interactive detection runs on mouseover/out — not on every mousemove —
 * so it stays smooth and never janks the page.
 */
const SELECTOR = 'a, button, [role="button"], input, textarea, select, label, [data-cursor]';

export const CustomCursor = () => {
  const dotRef = useRef(null);
  const ringRef = useRef(null);
  const touch = useIsTouch();
  const reduced = usePrefersReducedMotion();

  useEffect(() => {
    if (touch || reduced) {
      document.documentElement.classList.remove("has-custom-cursor");
      return;
    }
    document.documentElement.classList.add("has-custom-cursor");

    let mx = window.innerWidth / 2;
    let my = window.innerHeight / 2;
    let rx = mx;
    let ry = my;
    let hovering = false;
    let down = false;
    let raf;

    const onMove = (e) => {
      mx = e.clientX;
      my = e.clientY;
      // dot tracks the pointer exactly (no lag)
      const dot = dotRef.current;
      if (dot) dot.style.transform = `translate(${mx}px, ${my}px) translate(-50%, -50%)`;
    };
    const onOver = (e) => {
      if (e.target && e.target.closest && e.target.closest(SELECTOR)) hovering = true;
    };
    const onOut = (e) => {
      const from = e.target && e.target.closest && e.target.closest(SELECTOR);
      const to = e.relatedTarget && e.relatedTarget.closest && e.relatedTarget.closest(SELECTOR);
      if (from && !to) hovering = false;
    };
    const onDown = () => { down = true; };
    const onUp = () => { down = false; };

    const lerp = (a, b, n) => a + (b - a) * n;
    const animate = () => {
      rx = lerp(rx, mx, 0.35);
      ry = lerp(ry, my, 0.35);
      const ring = ringRef.current;
      if (ring) {
        const s = (hovering ? 1.7 : 1) * (down ? 0.82 : 1);
        ring.style.transform = `translate(${rx}px, ${ry}px) translate(-50%, -50%) scale(${s})`;
        ring.style.borderColor = hovering ? "rgba(255,104,87,0.7)" : "rgba(255,104,87,0.4)";
      }
      raf = requestAnimationFrame(animate);
    };
    raf = requestAnimationFrame(animate);

    window.addEventListener("mousemove", onMove, { passive: true });
    document.addEventListener("mouseover", onOver, true);
    document.addEventListener("mouseout", onOut, true);
    window.addEventListener("mousedown", onDown);
    window.addEventListener("mouseup", onUp);

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("mousemove", onMove);
      document.removeEventListener("mouseover", onOver, true);
      document.removeEventListener("mouseout", onOut, true);
      window.removeEventListener("mousedown", onDown);
      window.removeEventListener("mouseup", onUp);
      document.documentElement.classList.remove("has-custom-cursor");
    };
  }, [touch, reduced]);

  if (touch || reduced) return null;

  return (
    <>
      <div
        ref={ringRef}
        data-testid={CURSOR.root}
        aria-hidden="true"
        className="pointer-events-none fixed left-0 top-0 z-[200] h-8 w-8 rounded-full"
        style={{ border: "1.5px solid rgba(255,104,87,0.4)", willChange: "transform" }}
      />
      <div
        ref={dotRef}
        aria-hidden="true"
        className="pointer-events-none fixed left-0 top-0 z-[201] h-[6px] w-[6px] rounded-full"
        style={{ background: "#FF6857", boxShadow: "0 0 8px rgba(255,104,87,0.5)", willChange: "transform" }}
      />
    </>
  );
};
